import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import DragDropFileUpload from '../components/DragDropFileUpload';
import { assetFileDisplayName } from '../utils/assetDisplayName';
import { restrictedVideoProps } from '../utils/restrictedVideo';
import './Dashboard.css';

const Dashboard = () => {
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const [activeTab, setActiveTab] = useState('lessons');
  const [dashboardUser, setDashboardUser] = useState(null);
  const [lessons, setLessons] = useState([]);
  const [workshopPlans, setWorkshopPlans] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [isFreeTrial, setIsFreeTrial] = useState(false);
  const [messages, setMessages] = useState([]);
  const [userInput, setUserInput] = useState('');
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isReading, setIsReading] = useState(false);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        
        if (!user?.isAdmin && !user?.isOrgAdmin && !user?.isSchAdmin) {
          const response = await axios.get('/api/dashboard', {
            headers: {
              'Accept': 'application/json'
            },
            withCredentials: true
          });
          const { user: dashboardUserData, lessons, workshopPlans, subscriptions, isFreeTrial } = response.data;
          setDashboardUser(dashboardUserData);
          setLessons(lessons);
          setWorkshopPlans(workshopPlans);
          setSubscriptions(subscriptions);
          setIsFreeTrial(isFreeTrial);
        } else {
        }
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      }
    };

    // Only run if user is loaded and not an admin
    if (user && !user?.isAdmin && !user?.isOrgAdmin && !user?.isSchAdmin) {
      fetchDashboardData();
    }
  }, [user]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Error logging out:', error);
      navigate('/login');
    }
  };

  const handleWorkshopDate = async (planId) => {
    const dateInput = document.getElementById(`workshopDate${planId}`);
    if (!dateInput.value) {
      alert('Please select a date for the workshop');
      return;
    }

    try {
      // Use the existing addToBasket function from the backend
      await axios.post(`/api/update-basket`, {
        planId: planId,
        workshopDate: dateInput.value
      }, {
        withCredentials: true
      });
      // Handle successful addition to basket
    } catch (error) {
      console.error('Error adding workshop to basket:', error);
    }
  };

  const toggleAutoRenew = async (subscriptionId) => {
    try {
      // Use the existing update-auto-renew-status endpoint
      await axios.post(`/api/update-auto-renew-status`, {
        subscriptionId: subscriptionId,
        renew: !subscriptions.find(sub => sub._id === subscriptionId)?.autoRenew
      }, {
        withCredentials: true
      });
      // Refresh subscriptions after toggle
      const response = await axios.get('/api/dashboard', {
        headers: {
          'Accept': 'application/json'
        },
        withCredentials: true
      });
      setSubscriptions(response.data.subscriptions);
    } catch (error) {
      console.error('Error toggling auto-renew:', error);
    }
  };

  const handleFileUpload = (files) => {
    setUploadedFiles(files);
  };

  const handleFileRemove = () => {
    setUploadedFiles([]);
  };

  const sendMessage = async () => {
    if (!userInput.trim() && uploadedFiles.length === 0) return;

    const formData = new FormData();
    formData.append('message', userInput);
    uploadedFiles.forEach(file => {
      formData.append('files', file);
    });

    try {
      // Use the existing aiAgent endpoint
      const response = await axios.post('/api/aiAgent', formData, {
        withCredentials: true
      });
      setMessages(prev => [...prev, { type: 'user', content: userInput }, { type: 'ai', content: response.data.message }]);
      setUserInput('');
      setUploadedFiles([]);
    } catch (error) {
      console.error('Error sending message:', error);
    }
  };

  const startVoiceInput = () => {
    if ('webkitSpeechRecognition' in window) {
      const recognition = new window.webkitSpeechRecognition();
      recognition.continuous = false;
      recognition.interimResults = false;

      recognition.onresult = (event) => {
        const transcript = event.results[0][0].transcript;
        setUserInput(transcript);
      };

      recognition.start();
    } else {
      alert('Voice input is not supported in your browser');
    }
  };

  const toggleReading = () => {
    setIsReading(!isReading);
    if (!isReading) {
      const lastMessage = messages[messages.length - 1];
      if (lastMessage && lastMessage.type === 'ai') {
        const utterance = new SpeechSynthesisUtterance(lastMessage.content);
        window.speechSynthesis.speak(utterance);
      }
    } else {
      window.speechSynthesis.cancel();
    }
  };

  // Show loading while checking user role
  if (!user) {
    return <div>Loading...</div>;
  }

  // Immediate redirect for admin users - this should happen before any rendering
  
  if (user?.isAdmin) {
    navigate('/admin-dashboard');
    return <div>Redirecting to Admin Dashboard...</div>;
  } else if (user?.isOrgAdmin) {
    navigate('/org-admin-dashboard');
    return <div>Redirecting to Org Admin Dashboard...</div>;
  } else if (user?.isSchAdmin) {
    navigate('/school-admin-dashboard');
    return <div>Redirecting to School Admin Dashboard...</div>;
  }

  
  return (
    <div className="container">
      <h1 className="centered-header">{dashboardUser?.schoolName}'s Dashboard</h1>
      <div id="connection-status" style={{ color: 'red', fontWeight: 'bold' }}></div>

      <ul className="nav nav-underline" id="dashboardTabs" role="tablist">
        <li className="nav-item" role="presentation">
          <button
            className={`nav-link ${activeTab === 'lessons' ? 'active' : ''}`}
            onClick={() => setActiveTab('lessons')}
          >
            Lessons
          </button>
        </li>
        <li className="nav-item" role="presentation">
          <button
            className={`nav-link ${activeTab === 'workshops' ? 'active' : ''}`}
            onClick={() => setActiveTab('workshops')}
          >
            Workshops
          </button>
        </li>
        <li className="nav-item" role="presentation">
          <button
            className={`nav-link ${activeTab === 'subscriptions' ? 'active' : ''}`}
            onClick={() => setActiveTab('subscriptions')}
          >
            Subscriptions
          </button>
        </li>
        <li>
          <button onClick={handleLogout} className="nav-link">Logout</button>
        </li>
      </ul>

      <div className="tab-content">
        {activeTab === 'lessons' && (
          <div className="tab-pane fade show active">
            <br />
            {lessons && lessons.length > 0 ? (
              <div className="row">
                {lessons.map((lesson, index) => (
                  <React.Fragment key={lesson._id}>
                    <div className="col-md-4">
                      <div className={`card ${isFreeTrial && lesson.name.split(' ')[1] !== '1' ? 'lesson-greyed-out' : ''}`}>
                        <div className="card-body">
                          <h5 className="card-title">{lesson.name}</h5>
                          <p className="card-text">{lesson.description}</p>
                          {lesson.video?.thumbnail && (
                            <img
                              src={lesson.video.thumbnail}
                              alt={`Thumbnail for ${lesson.name}`}
                              className="asset-thumbnail thumbnail"
                              data-asset-id={lesson._id}
                              style={{ cursor: 'pointer', display: 'block' }}
                            />
                          )}
                          {lesson.file?.url && (
                            <video
                              controls
                              {...restrictedVideoProps}
                              height="200"
                              width="280"
                              className="hidden asset-video"
                              id={`video-${lesson._id}`}
                              data-asset-id={lesson._id}
                              style={{ display: 'none' }}
                            >
                              <source src={lesson.file.url} type="video/mp4" />
                              Your browser does not support the video tag.
                            </video>
                          )}
                          {lesson.documents && lesson.documents.length > 0 && (
                            <>
                              <h5>Teachers Guide:</h5>
                              {lesson.documents.map((document, docIndex) => (
                                <a key={docIndex} href={document.url} target="_blank" rel="noopener noreferrer">
                                  <i className="fas fa-file-pdf"></i> {assetFileDisplayName(document)}
                                </a>
                              ))}
                            </>
                          )}
                        </div>
                      </div>
                      <br />
                    </div>
                    {(index + 1) % 3 === 0 && <div className="w-100" />}
                  </React.Fragment>
                ))}
              </div>
            ) : (
              <p>No lessons available.</p>
            )}
          </div>
        )}

        {activeTab === 'workshops' && (
          <div className="tab-pane fade show active">
            <br />
            {workshopPlans && workshopPlans.length > 0 ? (
              <div className="row">
                {workshopPlans.map((plan, index) => (
                  <React.Fragment key={plan._id}>
                    <div className="col-md-4">
                      <div className="card">
                        <div className="card-body">
                          <div className="card-content">
                            <h5>{plan.name}</h5>
                            {plan.duration < 30 ? (
                              <p>Duration: {plan.duration} days</p>
                            ) : plan.duration === 365 ? (
                              <p>Duration: 1 year</p>
                            ) : (
                              <p>Duration: {Math.ceil(plan.duration / 30)} months</p>
                            )}
                            <p>Price: {plan.price}</p>
                            <p className="preserve-whitespace">{plan.description}</p>
                            {plan.type === 'workshop' && (
                              <div>
                                <p>Select Date: <label htmlFor={`workshopDate${plan._id}`}></label></p>
                                <input
                                  type="date"
                                  id={`workshopDate${plan._id}`}
                                  name="workshopDate"
                                  className="form-control"
                                />
                              </div>
                            )}
                            <br />
                            <button
                              className="btn btn-primary buyWorkshopButton"
                              onClick={() => handleWorkshopDate(plan._id)}
                            >
                              Buy Workshop
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                    {(index + 1) % 3 === 0 && <div className="w-100" />}
                  </React.Fragment>
                ))}
              </div>
            ) : (
              <p>No workshop plans available.</p>
            )}
          </div>
        )}

        {activeTab === 'subscriptions' && (
          <div className="tab-pane fade show active">
            <br />
            {subscriptions && subscriptions.length > 0 ? (
              subscriptions.map(subscription => (
                <div
                  key={subscription._id}
                  className={`rounded p-3 mb-3 ${
                    subscription.status === 'expired' || subscription.status === 'cancelled'
                      ? 'greyed-out'
                      : ''
                  }`}
                >
                  <p><b>Plan Name:</b> {subscription.planName}</p>
                  {subscription.documentUrl && !dashboardUser?.orgCode && (
                    <p>
                      <b>Invoice/Receipt:</b>{' '}
                      <a href={subscription.documentUrl} target="_blank" rel="noopener noreferrer">
                        View Invoice/Receipt
                      </a>
                    </p>
                  )}
                  {subscription.planType === 'workshop' ? (
                    <>
                      <p>
                        <b>Purchase Date:</b>{' '}
                        {new Date(subscription.startDate).toLocaleDateString('en-GB', {
                          weekday: 'short',
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })}
                      </p>
                      <p>
                        <b>Workshop Date:</b>{' '}
                        {new Date(subscription.workshopDate).toLocaleDateString('en-GB', {
                          weekday: 'short',
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })}
                      </p>
                    </>
                  ) : (
                    <>
                      <p>
                        <b>Start Date:</b>{' '}
                        {new Date(subscription.startDate).toLocaleDateString('en-GB', {
                          weekday: 'short',
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })}
                      </p>
                      <p>
                        <b>End Date:</b>{' '}
                        {new Date(subscription.endDate).toLocaleDateString('en-GB', {
                          weekday: 'short',
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })}
                      </p>
                      {subscription.status !== 'expired' && subscription.status !== 'cancelled' && (
                        <div className="form-check d-flex align-items-center">
                          <input
                            className="form-check-input autoRenewCheckbox"
                            type="checkbox"
                            id={`autoRenew${subscription._id}`}
                            checked={subscription.autoRenew}
                            onChange={() => toggleAutoRenew(subscription._id)}
                          />
                          <label className="form-check-label ml-2 light-orange-text" htmlFor={`autoRenew${subscription._id}`}>
                            <b>Auto Renew</b>
                          </label>
                          <div className="ml-auto">
                            {subscription.planName === '30-day FREE trial' ? (
                              <button
                                className="btn btn-primary"
                                onClick={() => navigate('/upgrade')}
                              >
                                Upgrade Subscription
                              </button>
                            ) : (
                              <button
                                className="btn btn-primary"
                                onClick={() => navigate('/renew')}
                              >
                                Renew Subscription
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              ))
            ) : (
              <p>No subscriptions available.</p>
            )}
          </div>
        )}

        {activeTab === 'aiagent' && (
          <div className="tab-pane fade show active">
            <br />
            <div className="row justify-content-center">
              <div className="col-md-10">
                <p className="light-orange-text">Our Black History AI Agent is here to help you!</p>
                <div id="messages" className="messages-container">
                  {messages.map((message, index) => (
                    <div
                      key={index}
                      className={`message ${message.type === 'user' ? 'user-message' : 'ai-message'}`}
                    >
                      {message.content}
                    </div>
                  ))}
                </div>
                <br />
                <form id="aiAgentForm" encType="multipart/form-data" className="ai-agent-form">
                  <textarea
                    id="userInput"
                    name="message"
                    placeholder="Type your question..."
                    value={userInput}
                    onChange={(e) => setUserInput(e.target.value)}
                    className="message-input"
                  />
                  <div className="form-controls">
                    <DragDropFileUpload
                      onFileSelected={handleFileUpload}
                      onFileRemoved={handleFileRemove}
                      selectedFile={uploadedFiles.length > 0 ? uploadedFiles[0] : null}
                      accept=".txt,.pdf,.docx"
                      multiple={true}
                      maxFileSize={10 * 1024 * 1024} // 10MB
                      supportedTypes={['.txt', '.pdf', '.docx']}
                      placeholder="Click to upload files or drag and drop"
                      description="Supports: TXT, PDF, DOCX files"
                    />
                    <div className="button-group">
                      <button type="button" className="btn btn-primary" onClick={sendMessage}>
                        Submit
                      </button>
                      <button type="button" className="btn btn-secondary" onClick={startVoiceInput}>
                        🎤 Speak
                      </button>
                      <button
                        className={`btn ${isReading ? 'btn-danger' : 'btn-secondary'}`}
                        onClick={toggleReading}
                      >
                        {isReading ? 'Stop Reading' : 'Read'}
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard; 